import React, {useEffect, useMemo, useRef, useState} from 'react';
import {connect} from 'react-redux';
import Config from 'app/Config';
import {prepareSameWindowUrl, buildUrlAndOpenWindow} from '../../../../../utilities/StringHelpers/StringHelpers';
import {reportDWH} from '../../OperationsAreasBlockSlice';
import './operationAreas.scss';

const PRESS_EFFECT_DURATION = 500;

const OperationAreas = (props) => {
  const [openItems, setOpenItems] = useState({});
  const [expandedActionItems, setExpandedActionItems] = useState({});
  const [pressedItems, setPressedItems] = useState({});
  const pressTimeoutsRef = useRef({});

  useEffect(() => {
    const pressTimeouts = pressTimeoutsRef.current;

    return () => {
      Object.values(pressTimeouts).forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, []);

  const triggerPressEffect = (index) => {
    setPressedItems((prev) => ({...prev, [index]: true}));

    if (pressTimeoutsRef.current[index]) {
      clearTimeout(pressTimeoutsRef.current[index]);
    }

    pressTimeoutsRef.current[index] = setTimeout(() => {
      setPressedItems((prev) => ({...prev, [index]: false}));
      delete pressTimeoutsRef.current[index];
    }, PRESS_EFFECT_DURATION);
  };

  const operationAreas = useMemo(() => props.operationAreas ?? [], [props.operationAreas]);

  const filteredOperationAreas = useMemo(() => {
    const subscriberProducts = props._subscribersByProduct ?? {};
    const subscriberProductKeys = Object.keys(subscriberProducts);

    return operationAreas.filter((area) => {
      const actionTypes = area?.actionType ?? [];

      if (subscriberProductKeys.length === 0) {
        return actionTypes.includes('ProductsNotExist');
      }

      return actionTypes.some((type) => subscriberProductKeys.includes(type));
    });
  }, [operationAreas, props._subscribersByProduct]);

  const toggleItem = (index) => {
    setOpenItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const expandActionItems = (areaIndex) => {
    setExpandedActionItems((prev) => ({
      ...prev,
      [areaIndex]: true,
    }));
  };

  const getImageUrl = (image) => {
    const imageUrl = image?.url || image?.value?.url || image;

    if (!imageUrl) {
      return null;
    }

    if (/^https?:\/\//i.test(imageUrl)) {
      return imageUrl;
    }

    return `${Config?.epiBaseUrl ?? ''}${imageUrl}`;
  };

  const handleAreaItemClick = (index, area, hasActionItems) => {
    triggerPressEffect(index);

    if (!hasActionItems) {
      return;
    }

    const isCurrentlyOpen = !!openItems[index];

    if (!isCurrentlyOpen) {
      props.reportDWH?.({
        actionType: 'IWantToTakeAnActionProcessLobby',
        status: 'Ongoing',
        messageText: 'Click On An Product Button',
        param1: area?.areaName ?? '',
      });
    }

    toggleItem(index);
  };


  const handleActionItemClick = (event, item, areaName) => {
    event.preventDefault();
    event.stopPropagation();
    const link = item?.link;

    props.reportDWH?.({
      actionType: 'IWantToTakeAnActionProcessLobby',
      status: 'Completed',
      messageText: 'Click On An Action Button',
      param1: areaName ?? '',
      param3: item?.linkTitle ?? '',
    });

    if (!link || typeof link !== 'string') {
      return null;
    }

    const isPdfLink = /\.pdf($|\?)/i.test(link);

    if (isPdfLink) {
      buildUrlAndOpenWindow(link);
      return;
    }

    prepareSameWindowUrl(link, props.context);
  };

  if (!filteredOperationAreas.length) {
    return null;
  }

  return (
    <div className='operation-areas-list-wrapper'>
      <div className='operation-areas-list'>
        {filteredOperationAreas.map((area, index) => {
          const isOpen = !!openItems[index];
          const isPressed = !!pressedItems[index];
          const actionItems = area?.actionItems ?? [];
          const hasActionItems = actionItems.length > 0;
          const iconUrl = getImageUrl(area?.icon);

          return (
            <div
              key={`${area?.areaName ?? 'operation-area'}-${index}`}
              className={`operation-area-item ${isOpen ? 'is-open' : ''} ${isPressed ? 'is-pressed' : ''}`}
              onClick={() => handleAreaItemClick(index, area, hasActionItems)}
              role='button'>
              <div className='operation-area-item-header'>
                <div className='operation-area-item-main'>
                  {iconUrl ? <img className='operation-area-item-icon' src={iconUrl} alt={area?.areaName ?? 'icon'} /> : null}
                  <span className='operation-area-item-title'>{area?.areaName}</span>
                </div>
                <button
                  type='button'
                  className={`operation-area-toggle-button ${isOpen ? 'is-open' : ''}`}
                  aria-expanded={isOpen}
                  aria-label={isOpen ? 'collapse area actions' : 'expand area actions'}
                  disabled={!hasActionItems}>
                  <svg className='operation-area-toggle-arrow' xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                    <path d='M14.9511 6.97635C15.3703 6.53408 16.0189 6.56994 16.4 7.05643C16.7517 7.50551 16.7524 8.18159 16.4208 8.6308L16.331 8.73811L10.69 14.6905C10.3314 15.0689 9.79946 15.1004 9.41198 14.7851L9.31012 14.6905L3.6691 8.73811C3.24997 8.29584 3.21908 7.54293 3.60011 7.05643C3.95183 6.60736 4.53159 6.54226 4.94837 6.88276L5.04894 6.97635L9.99901 12.2013L14.9511 6.97635Z' fill='white'/>
                  </svg>
                </button>
              </div>

              {isOpen && hasActionItems && (
                <div className='operation-area-item-collapse'>
                  {(() => {
                    const threshold = area?.showMoreThreshold;
                    const isAreaExpanded = !!expandedActionItems[index];
                    const hasThreshold = Number.isFinite(Number(threshold)) && Number(threshold) > 0;
                    const showAll = !hasThreshold || isAreaExpanded;
                    const visibleItems = showAll ? actionItems : actionItems.slice(0, Number(threshold));
                    const hasHiddenItems = hasThreshold && !isAreaExpanded && actionItems.length > Number(threshold);

                    return (
                      <>
                        {visibleItems.map((item, itemIndex) => (
                          <div
                            key={`${item?.linkTitle ?? 'action-item'}-${itemIndex}`}
                            className='operation-area-action-item'
                            onClick={(event) => handleActionItemClick(event, item, area?.areaName)}>
                            <span className='button-title'>{item?.linkTitle}</span>
                            {item?.showBadge && item?.badgeTitle ? <span className='button-badge'>{item.badgeTitle}</span> : null}
                          </div>
                        ))}
                        {hasHiddenItems && (
                          <button
                            type='button'
                            className='operation-area-show-more-button'
                            onClick={(event) => {
                              event.stopPropagation();
                              expandActionItems(index);
                            }}>
                            <span className='show-more-title'>{'עוד פעולות'}</span>
                            <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                              <path d='M8 3C8.55228 3 9 3.44772 9 4V7H12C12.5523 7 13 7.44772 13 8C13 8.55228 12.5523 9 12 9H9V12C9 12.5523 8.55228 13 8 13C7.44772 13 7 12.5523 7 12V9H4C3.44772 9 3 8.55228 3 8C3 7.44772 3.44771 7 4 7H7V4C7 3.44772 7.44772 3 8 3Z' fill='#810EA9'/>
                            </svg>
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    operationAreas: state.operationsAreasBlock?.blockData?.operationAreas,
    _subscribersByProduct: state.selfcarePage?.subscribersByProduct,
  };
};

const mapDispatchToProps = {
  reportDWH,
};

export default connect(mapStateToProps, mapDispatchToProps)(OperationAreas);
